import { useTranslation } from 'next-i18next/pages';
import cn from 'clsx';
import { getUploadcareImageTransformationProps } from '@pkgs/components/utils/getUploadcareImageTransformationProps';
import { Carousel, CarouselImage, type UseCarouselActions, AiBadge, getImageAltWithAiIndicator, type TFunction } from '@pkgs/components';
import { getUploadCareImageUrl, type ImageV2 } from '@pkgs/api';

import { getAiBadgeTranslationKey } from '@/utils/aiBadgeUtils';
import { TRANSLATION_NAMESPACE } from '@/i18n/config';
import { ImageComponent } from '@/components/common/ImageComponent/ImageComponent';

import styles from './ExposeHeroImage.module.css';

type Props = {
  images: ImageV2[];
  carouselActions: UseCarouselActions;
  hasWaterMark: boolean;
  isPrivateOffice?: boolean;
  onImageClick: () => void;
  showsThumb?: boolean;
  isRestrictedProperty?: boolean;
  shouldUnblurFirstImage?: boolean;
  isFirstImageBlurred?: boolean;
  shouldPreload?: boolean;
};

export const PropertyImageSlider = ({
  images,
  carouselActions,
  hasWaterMark,
  showsThumb = true,
  isPrivateOffice,
  isRestrictedProperty,
  onImageClick,
  isFirstImageBlurred,
  shouldUnblurFirstImage,
  shouldPreload = true,
}: Props) => {
  const { t } = useTranslation(TRANSLATION_NAMESPACE.property_expose);

  return (
    <Carousel carouselActions={carouselActions} carouselThumbProps={{ isVisible: showsThumb }}>
      {images.map(({ id, title, altText, focalPoint, width, height, aiType }, carouselIndex) => (
        <CarouselImage
          key={id}
          className={cn(
            styles.carouselImage,
            isPrivateOffice && styles.carouselImagePrivateOffice,
            isRestrictedProperty && styles.carouselImageRestricted,
          )}
          data-first-image-blurred={carouselIndex === 0 && isFirstImageBlurred === true}
          data-should-unblur-first-image={carouselIndex === 0 && shouldUnblurFirstImage === true}
          focalPoint={focalPoint}
          data-testid={`property-image-${carouselIndex}`}
          sizes="100vw"
          lazyLoadImage={carouselIndex > 0}
          src={getUploadCareImageUrl({ imageId: id, useWaterMark: hasWaterMark })}
          alt={getImageAltWithAiIndicator({ baseAlt: altText || title, imageAiType: aiType, t: t as TFunction })}
          shouldPreloadImage={shouldPreload && carouselIndex === 0}
          height={height ?? 240}
          width={width ?? 360}
          onClick={onImageClick}
          inView={carouselActions.slidesInView.includes(carouselIndex)}
          imageComponent={ImageComponent}
          carouselIndex={carouselIndex}
          aiType={aiType}
          {...(isPrivateOffice && {
            ...getUploadcareImageTransformationProps({
              aspectRatio: '3/2',
              fit: 'automatic',
              image: { focalPoint, height, width },
            }),
            uploadcareMediaAspectRatio: '2/1',
            uploadcareMediaQuery: '(min-width: 1240px)',
          })}
          aiBadge={
            !isFirstImageBlurred ? (
              <AiBadge
                aiType={aiType}
                t={t as TFunction}
                translationKey={getAiBadgeTranslationKey({ size: 'm' })}
                size="m"
                offset="m"
                side="left"
                isPrivateOffice={isPrivateOffice}
              />
            ) : undefined
          }
        />
      ))}
    </Carousel>
  );
};
